// @flow
import React, { type Node, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';
import path from 'ramda/es/path';

import ElJurDnevnik from 'app/components/ElJurDnevnik';
import Nope from 'app/components/common/Nope';
import StudentsSelector from 'app/components/StudentsSelector';
import Loading from 'app/components/Loader';
import { APP_DNEVNIK } from 'app/common/constants/services';
import getAppTranslateIdWithPrefix from 'app/components/Subs/i18n/getAppTranslateIdWithPrefix';
import * as orgsTypes from 'app/common/constants/schools/types';
import CommonPage from 'app/common/templates/CommonPage';
import { getIsAppConnected, getIsAppSubscribed } from 'app/selectors/applications';

import selectIsDnevnik from './selectIsDnevnik';
import selectDnevnikType from './selectDnevnikType';
import RestrictIdentifier from '../RestrictIdentifier';


type Props = {
  studentId: string,
  children: Node,
}

const DnevnikRoot = ({ studentId, children }: Props): Node => {
  const { t } = useTranslation();

  const isSchoolSupport: boolean = useSelector(state => selectIsDnevnik(state, { studentId }));
  const dnevnikType: string = useSelector(state => selectDnevnikType(state, { studentId }));
  const orgType: $Values<typeof orgsTypes> | void = useSelector(path(['students', 'data', studentId, 'orgType']), shallowEqual);
  const appTranslateId = getAppTranslateIdWithPrefix(orgType || 'unknown', APP_DNEVNIK);

  const isConnected = useSelector(state => getIsAppConnected(state, { id: studentId, app: APP_DNEVNIK }));
  const sub = useSelector(state => getIsAppSubscribed(state, { id: studentId, app: APP_DNEVNIK }));

  const { loading: loadingOpts } = useSelector(state => state.apps.opts, shallowEqual);
  const { loading: loadingStudents } = useSelector(state => state.students, shallowEqual);
  const loading = loadingOpts || loadingStudents;

  const hasIdentifier = !useSelector(path(['students', 'data', studentId, 'appsBlocked']), shallowEqual);

  if (loading) {
    return <Loading />;
  }

  return (
    <CommonPage
      header={t(`subs:${appTranslateId}`)}
      headerSuffix={dnevnikType === 'eljur' ? ' (ЭлЖур)' : ''}
    >
      {/* селектор учеников, когда нет доступа к дневнику */}
      {(!isSchoolSupport || !isConnected || !hasIdentifier)
        && <StudentsSelector />}

      {!isSchoolSupport
        && <Nope
          service={APP_DNEVNIK}
          text={t('dnevnik:not_provided', 'В школе ученика не предусмотрено использование приложения электронного дневника')}
        />}

      {isSchoolSupport
        && dnevnikType === 'eljur'
        && <ElJurDnevnik>{children}</ElJurDnevnik>}

      {isSchoolSupport
        && dnevnikType === 'internal'
        && <Fragment>
          {!isConnected
            && <Nope service={APP_DNEVNIK} sub={sub} />}

          {isConnected
            && !hasIdentifier
            && <RestrictIdentifier studentId={studentId} />}

          {isConnected
            && hasIdentifier
            && children}
        </Fragment>}
    </CommonPage>
  );
};

export default DnevnikRoot;
